<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="!isLoading && booking" class="flex grow flex-col items-center justify-center">
    <template v-if="isCounterHandover">
      <div class="w-full bg-white p-4 md:p-6">
        <ScaledContent>
          <div class="w-[210mm]" v-html="contractHtml"></div>
        </ScaledContent>
      </div>
      <div v-if="signatureImageUrl" class="w-full bg-white px-4 pb-8 md:px-6">
        <div class="ml-auto w-full max-w-[450px]">
          <div
            class="relative flex h-[140px] w-full items-end overflow-visible border-b border-black"
          >
            <img
              :src="signatureImageUrl"
              alt=""
              class="max-h-[120px] w-full object-contain object-right"
            />
          </div>
          <p class="mt-1 text-right">{{ t('signature') }}</p>
        </div>
      </div>
      <div class="card-actions mt-8 grid w-full gap-4 md:flex md:justify-end">
        <p v-if="!signatureReady" class="text-sm text-gray-600 md:mr-auto">
          {{ t('waitingForSignature') }}
        </p>
        <CVButton
          variant="default"
          :disabled="!signatureReady || isSaving"
          :is-loading="isSaving"
          @click="completeCounterHandover"
        >
          {{ t('completeHandover') }}
        </CVButton>
      </div>
    </template>
    <template v-else>
      <div class="w-full bg-white p-4 md:p-6">
        <ScaledContent>
          <div class="w-[210mm]" v-html="contractHtml"></div>
        </ScaledContent>
      </div>
      <div class="w-full bg-white pb-8 pl-4 pr-4 md:pl-6 md:pr-6">
        <div class="relative z-20 mb-4 space-y-5">
          <Checkbox v-model="gtcConfirmed" :label="t('confirmGtc')" />
          <Checkbox v-model="iTakePicturesConfirmed" :label="t('confirmITakePictures')" />
          <Checkbox
            v-if="isSportsCar"
            v-model="noRacetracksConfirmed"
            :label="t('confirmNoRacetracks')"
          />
          <Checkbox
            v-for="(customCheckbox, index) in customCheckboxes"
            :key="customCheckbox"
            :label="customCheckbox"
            @change="customCheckboxesConfirmed[index] = $event"
          />
        </div>

        <div
          class="-mx-6 -mt-10 grid overflow-clip px-6 pb-10 pt-10 md:flex md:items-end md:justify-between"
        >
          <div class="relative z-20">
            <div
              v-date="{
                value: DateTime.now().toISO(),
                format: DateTime.DATE_FULL,
              }"
              class="border-b border-black pb-2"
            ></div>
            <p class="mt-1">{{ t('date') }}</p>
          </div>
          <div class="max-w-[450px] flex-1 overflow-visible">
            <div
              class="relative flex h-[140px] w-full items-stretch overflow-visible border-b border-black"
            >
              <div
                v-if="signingState === SigningState.NOTREADY"
                class="flex h-full w-full items-center bg-gray-200 p-2 font-medium text-gray-500"
              >
                <span class="text-center">
                  {{ t('pleaseConfirm') }}
                </span>
              </div>

              <canvas
                v-show="
                  signingState === SigningState.ACTIVE || signingState === SigningState.FILLED
                "
                ref="signatureCanvasRef"
                class="absolute ml-[-70px] mr-[-70px] mt-[-80px] h-[340px] w-[560px] touch-none"
                width="560"
                height="340"
                data-testid="signature-canvas"
              ></canvas>
            </div>
            <p class="relative z-20 mt-1 text-right">{{ t('signature') }}</p>
          </div>
        </div>
        <p class="text-center text-xs">
          {{ t('imprint') }}
        </p>
      </div>

      <div
        ref="buttonRow"
        class="card-actions relative z-20 mt-8 grid w-full gap-4 md:flex md:justify-end"
      >
        <CVButton
          v-show="!isSaving && signingState !== SigningState.NOTREADY"
          variant="ghost"
          @click="clearSignature"
        >
          {{ t('resetSignature') }}
        </CVButton>
        <CVButton
          variant="default"
          :disabled="signingState !== SigningState.FILLED || isSaving"
          :is-loading="isSaving"
          @click="saveSignature"
        >
          {{ t('completeHandover') }}
        </CVButton>
      </div>
    </template>
  </div>
  <div v-else class="flex items-center justify-center py-10">
    <Spinner />
  </div>
</template>

<script lang="ts" setup>
import { VehicleCategory } from '@/entities/vehicle-category.enum';
import { useI18n } from 'vue-i18n';
import {
  useBooking,
  useBookingContractTemplate,
  useCompleteHandover,
} from '@/queries/use-bookings';
import { useUploadContractSignature } from '@/queries/use-upload';
import { DateTime } from 'luxon';
import { create as createDrawingBoard } from 'simple-drawing-board';
import { computed, nextTick, ref, type Ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useProcessNavigation, useProcessSessionContext } from '@/hooks/use-process-step-tracking';
import { QueryLocale } from '@/entities/auth/local.enum';
import { getLocalizedField } from '@/utils/get-localized-field';
import Checkbox from '@/components/Checkbox.vue';
import ScaledContent from '@/components/ScaledContent.vue';
import { usePhoneLandscapeMode } from '@/hooks/use-phone-landscape-mode';

enum SigningState {
  NOTREADY = 'NOTREADY',
  ACTIVE = 'ACTIVE',
  FILLED = 'FILLED',
}

const { t, locale } = useI18n();
const route = useRoute();
const { goToNext } = useProcessNavigation();
const { session, isCounterHandover } = useProcessSessionContext();
const signingState = ref(SigningState.NOTREADY);
const signatureCanvasRef = ref<HTMLCanvasElement>();
const drawingBoard = ref();
const buttonRow = ref<HTMLDivElement>();
const isSubmitting = ref(false);
const gtcConfirmed = ref(false);
const iTakePicturesConfirmed = ref(false);
const noRacetracksConfirmed = ref(false);
const customCheckboxesConfirmed = ref<boolean[]>([]);
const bookingId = route.params.id as string;

const { isLoading, data: contractHtml } = useBookingContractTemplate(
  bookingId,
  locale as Ref<QueryLocale>,
);

const { mutateAsync: completeHandover, isPending: isCompletingHandover } = useCompleteHandover();
const { mutateAsync: uploadSignature, isPending: isUploadingSignature } =
  useUploadContractSignature();

const isSaving = computed(
  () => isSubmitting.value || isCompletingHandover.value || isUploadingSignature.value,
);

usePhoneLandscapeMode();

watchEffect(() => {
  const allConfirmed =
    gtcConfirmed.value &&
    iTakePicturesConfirmed.value &&
    (noRacetracksConfirmed.value || !isSportsCar.value) &&
    customCheckboxesConfirmed.value.filter((confirmed) => confirmed).length ===
      customCheckboxes.value.length;

  if (allConfirmed) {
    if (signingState.value === SigningState.NOTREADY) {
      signingState.value = SigningState.ACTIVE;
    }
  } else {
    if (signingState.value !== SigningState.NOTREADY) {
      signingState.value = SigningState.NOTREADY;
    }
  }
});

watch(signingState, (newVal) => {
  if (!signatureCanvasRef.value) return;
  switch (newVal) {
    case SigningState.NOTREADY:
      drawingBoard.value?.clear();
      break;
    case SigningState.ACTIVE:
      drawingBoard.value = createDrawingBoard(signatureCanvasRef.value);
      drawingBoard.value.observer.on('drawEnd', () => {
        signingState.value = SigningState.FILLED;
      });
      // Scroll the action buttons into view when signing starts
      nextTick(() => {
        if (!buttonRow.value) return;
        const rect = buttonRow.value.getBoundingClientRect();
        const scrollingElement = document.scrollingElement || document.documentElement;
        const currentY = window.pageYOffset || scrollingElement.scrollTop || 0;
        const maxY = (scrollingElement.scrollHeight || 0) - window.innerHeight;
        const desiredY = currentY + rect.bottom - window.innerHeight + 30;
        const targetY = Math.max(0, Math.min(maxY, desiredY));
        if (targetY > currentY + 1) {
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
      });
      break;
  }
});

const { data: booking } = useBooking(bookingId);
const allocation = computed(() => booking.value?.allocations[0]);

const signatureReady = computed(
  () =>
    !!session.value?.gates?.customerSignatureCaptured &&
    !!session.value?.contract?.signatureFileKey,
);

const signatureImageUrl = computed(() => session.value?.contract?.signatureFileUrl);

const completeCounterHandover = async () => {
  if (!allocation.value || !booking.value || !signatureReady.value || isSaving.value) return;
  isSubmitting.value = true;
  try {
    await completeHandover({
      bookingId,
      data: {
        handedOverAllocationId: allocation.value.id,
        signatureFileKey: session.value!.contract!.signatureFileKey!,
      },
    });
    await goToNext();
  } finally {
    isSubmitting.value = false;
  }
};

const isSportsCar = computed(() => {
  const matchTypes = [VehicleCategory.SPORTS_CAR, VehicleCategory.SUPER_CAR];
  return matchTypes.some((i) => allocation.value?.car.vehicleType?.categories.includes(i));
});

const customCheckboxes = computed(() => {
  return allocation.value
    ? allocation.value.car.vehicleType.customContractCheckboxes.map((checkbox) =>
        getLocalizedField(checkbox.label, locale.value),
      )
    : [];
});

const clearSignature = () => {
  if (!drawingBoard.value || isSaving.value) return;

  drawingBoard.value.clear();
  signingState.value = SigningState.ACTIVE;
};

const getSignatureBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve));

const saveSignature = async () => {
  if (!signatureCanvasRef.value || signingState.value !== SigningState.FILLED || isSaving.value) {
    return;
  }

  isSubmitting.value = true;
  try {
    const blob = await getSignatureBlob(signatureCanvasRef.value);
    if (!allocation.value) {
      return;
    }
    const { bookingNumber } = booking.value ?? {};
    if (!blob || !bookingNumber || !booking.value) return;
    const res = await uploadSignature({
      file: blob,
      params: {
        bookingId: booking.value.id,
      },
    });

    await completeHandover({
      bookingId,
      data: {
        handedOverAllocationId: allocation.value.id,
        signatureFileKey: res.fileKey,
      },
    });

    await goToNext();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
h3 {
  @apply text-sm font-medium xl:text-base;
}
h4 {
  @apply text-xs font-medium xl:text-sm;
}
</style>

<i18n lang="json">
{
  "en": {
    "pleaseConfirm": "Please confirm the required declarations first",
    "confirmGtc": "I have read and accept the terms and conditions and privacy agreements.",
    "confirmITakePictures": "I take my own pictures of the vehicle and in particular the rims, the front bumper and the rear bumper to provide as evidence in the event of damage.",
    "confirmNoRacetracks": "I do not drive the rental car on race tracks (incl. \"tourist drives\").",
    "date": "Date",
    "resetSignature": "Reset Signature",
    "completeHandover": "Complete handover",
    "waitingForSignature": "Waiting for the customer to sign on the counter screen…",
    "signature": "Signature of renter/CarVia",
    "imprint": "Place of business: Munich, Germany • Commercial register: District court Munich: HRB 232676 • Managing directors: Joschka Reik, Julian Reik VAT-ID: DE312142017"
  },
  "de": {
    "pleaseConfirm": "Bitte bestätigen Sie zuerst die erforderlichen Erklärungen",
    "confirmGtc": "Ich habe die AGB und Datenschutzverbeinbarungen gelesen und akzeptiere diese.",
    "confirmITakePictures": "Ich mache eigene Bilder vom Fahrzeug und insbesondere den Felgen, der Frontsstoßstange und der Heckstoßstange, um sie im Schadensfall zum Beweis bereitzustellen.",
    "confirmNoRacetracks": "Ich fahre mit dem Mietwagen nicht auf Rennstrecken (inkl. \"Touristenfahrten\").",
    "date": "Datum",
    "resetSignature": "Unterschrift zurücksetzen",
    "completeHandover": "Übergabe abschließen",
    "waitingForSignature": "Warten auf die Unterschrift des Kunden am Schalter…",
    "signature": "Unterschrift Mieter/CarVia",
    "imprint": "Sitz der Gesellschaft: München • Handelsregister München: HRB 232676 • Geschäftsführer: Joschka Reik, Julian Reik • Ust.-ID: DE312142017"
  },
  "es": {
    "pleaseConfirm": "Confirme primero las declaraciones obligatorias",
    "confirmGtc": "He leído y acepto las condiciones generales y los acuerdos de privacidad.",
    "confirmITakePictures": "Tomo mis propias fotos del vehículo y en particular de las llantas, el paragolpes delantero y el trasero para aportarlas como prueba en caso de daños.",
    "confirmNoRacetracks": "No conduzco el vehículo de alquiler en circuitos (incl. «vueltas turísticas»).",
    "date": "Fecha",
    "resetSignature": "Restablecer firma",
    "completeHandover": "Completar entrega",
    "waitingForSignature": "Esperando la firma del cliente en el mostrador…",
    "signature": "Firma del arrendatario / CarVia",
    "imprint": "Sede: Múnich, Alemania • Registro mercantil: Tribunal de Múnich HRB 232676 • Directores: Joschka Reik, Julian Reik • NIF-IVA: DE312142017"
  }
}
</i18n>
